import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    computed,
    inject,
    signal
} from '@angular/core';
import { AixDataTestingDirective } from '@trade-platform/ui-components';
import { ProfileStoreFacade, PylonChatService } from '@advisor-ui/app-services';
import { Subscription } from 'rxjs';

/**
 * The persistent bottom-right support bubble (trade-platform/trade-platform#10468).
 *
 * It is a fallback ENTRYPOINT, not the Pylon widget: clicking it runs the same `openChat()` the nav
 * bar "Start a chat" item does, which is the first moment anything is requested from Pylon. That
 * keeps the on-demand privacy posture — a user who has never opened chat sees only this static
 * button and nothing loads from Pylon until they click it.
 *
 * Once the widget boots (via this click, or the page-load resume for a returning user), Pylon shows
 * its OWN launcher and unread-reply badge bottom-right, so this button hides to avoid stacking two
 * bubbles. `PylonChatService.booted` drives that handoff.
 */
@Component({
    selector: 'aix-pylon-launcher',
    templateUrl: './pylon-launcher.html',
    styleUrls: ['./pylon-launcher.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [AixDataTestingDirective]
})
export class AixPylonLauncherComponent implements OnInit, OnDestroy {
    private pylonChat = inject(PylonChatService);
    private profileStore = inject(ProfileStoreFacade);
    private subscription?: Subscription;

    /**
     * Tenant feature gate, refreshed whenever the profile loads — the same signal and timing the nav
     * bar uses for its "Start a chat" item, so the two entrypoints appear and disappear together.
     */
    private readonly enabled = signal(false);

    /**
     * Show our button only for an enabled tenant while the real Pylon widget has NOT booted. The
     * moment it boots, Pylon's own launcher (with the badge) takes over and this one hides, so they
     * never stack.
     */
    readonly visible = computed(() => this.enabled() && !this.pylonChat.booted());

    ngOnInit(): void {
        this.subscription = this.profileStore.profileSuccess$.subscribe(() => {
            this.enabled.set(this.pylonChat.isEnabled());
        });
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }

    /** Same path as the nav bar entrypoint: boot, identify and open the panel. Guards its own state. */
    open(): void {
        void this.pylonChat.openChat();
    }
}
